import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Home as HomeIcon, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import axiosInstance from '@/utils/axios';
import PropertyCard from '@/components/PropertyCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DEPARTMENTS = ['Cundinamarca', 'Antioquia', 'Valle del Cauca', 'Bolívar', 'Santander', 'Atlántico', 'Magdalena', 'Caldas', 'Risaralda', 'Quindío', 'Meta'];
const PROPERTY_TYPES = [
  { value: 'APARTMENT', label: 'Apartamento' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'LOT', label: 'Lote' },
  { value: 'FARM', label: 'Finca' },
  { value: 'OFFICE', label: 'Oficina' },
  { value: 'LOCAL', label: 'Local Comercial' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    department: '',
    operation: '',
    property_type: '',
  });
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      // Fetch featured properties
      const { data: allData } = await axiosInstance.get('/properties', {
        params: { limit: 100 }
      });
      const featured = allData.properties.filter(p => p.is_featured).slice(0, 3);
      setFeaturedProperties(featured);

      // Fetch recent properties
      const { data: recentData } = await axiosInstance.get('/properties', {
        params: { limit: 6, sort_by: 'created_at' }
      });
      setRecentProperties(recentData.properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchParams.department) params.append('department', searchParams.department);
    if (searchParams.operation) params.append('operation', searchParams.operation);
    if (searchParams.property_type) params.append('property_type', searchParams.property_type);
    navigate(`/buscar?${params.toString()}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-[600px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(29, 78, 216, 0.7), rgba(29, 78, 216, 0.85)), url('https://images.unsplash.com/photo-1706808849827-7366c098b317?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
        data-testid="hero-section"
      >
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white mb-6 tracking-tight leading-tight"
          >
            Encuentra el hogar de tus sueños
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-white/90 mb-12 leading-relaxed"
          >
            Miles de propiedades en venta y arriendo en toda Colombia
          </motion.p>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-2xl p-6 md:p-8"
            data-testid="search-box"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label htmlFor="department" className="text-slate-700 mb-2 block font-medium">Departamento</Label>
                <Select onValueChange={(value) => setSearchParams({ ...searchParams, department: value })}>
                  <SelectTrigger id="department" data-testid="search-department">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="operation" className="text-slate-700 mb-2 block font-medium">Operación</Label>
                <Select onValueChange={(value) => setSearchParams({ ...searchParams, operation: value })}>
                  <SelectTrigger id="operation" data-testid="search-operation">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SALE">Venta</SelectItem>
                    <SelectItem value="RENT">Arriendo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="property_type" className="text-slate-700 mb-2 block font-medium">Tipo de Propiedad</Label>
                <Select onValueChange={(value) => setSearchParams({ ...searchParams, property_type: value })}>
                  <SelectTrigger id="property_type" data-testid="search-property-type">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={handleSearch}
              className="w-full bg-primary hover:bg-primary-hover h-12 text-base font-semibold"
              data-testid="search-button"
            >
              <Search className="w-5 h-5 mr-2" />
              Buscar Propiedades
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Featured Properties */}
      {featuredProperties.length > 0 && (
        <section className="py-16 md:py-24 bg-slate-50" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center mb-8">
              <Sparkles className="w-6 h-6 text-accent mr-2" />
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900">Propiedades Destacadas</h2>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-96 bg-slate-200 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredProperties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recent Properties */}
      <section className="py-16 md:py-24" data-testid="recent-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center mb-8">
            <HomeIcon className="w-6 h-6 text-secondary mr-2" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900">Propiedades Recientes</h2>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 bg-slate-200 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;